/**
 * Durée minimale d'un infoservice posé par l'ajustement automatique. En dessous
 * d'une minute, le passage n'est plus une information mais un clignotement :
 * l'ajustement préfère renoncer et le dire plutôt que de poser dix secondes.
 */
export const INFOSERVICE_MIN_SEC = 60

/** Cadences éditoriales usuelles proposées par défaut dans le dialogue. */
export const REBROADCAST_CADENCE_PRESETS_MIN = [15, 30, 60, 90, 120, 180, 240, 360] as const

/**
 * Propose le repère usuel le plus proche de la durée réelle. Une plage de
 * 25 minutes suggère ainsi 30 minutes ; une soirée de 2 h 03 suggère 2 heures
 * et fera apparaître l'alerte de dépassement tant que son montage n'est pas
 * raccourci.
 */
export function suggestRebroadcastCadenceMinutes(durationSec: number): number {
  const durationMin = Math.max(1, durationSec / 60)
  return REBROADCAST_CADENCE_PRESETS_MIN.reduce((best, candidate) => (
    Math.abs(candidate - durationMin) < Math.abs(best - durationMin) ? candidate : best
  ))
}

/** Ce que l'ajustement a besoin de connaître d'un écart, et rien de plus. */
export interface InfoserviceBreak {
  key: string
  currentInfoserviceSec: number
}

/**
 * Répartit sur les écarts la durée d'infoservice qui manque — ou qui déborde —
 * pour que la boucle d'une rediffusion tombe juste.
 *
 * La répartition est au prorata des durées déjà en place : si l'opérateur a
 * donné deux minutes à un écart et six à un autre, l'ajustement conserve ce
 * rapport au lieu de tout ramener au même. Seuls les écarts qui portent déjà un
 * infoservice sont touchés — en poser un là où l'opérateur n'en voulait pas
 * changerait son montage, pas son minutage.
 *
 * `residualSec` rapporte ce que le plancher d'une minute a empêché d'absorber :
 * l'appelant doit le dire plutôt que de laisser croire à un calage exact.
 */
export function fitInfoserviceDurations(
  breaks: InfoserviceBreak[],
  current: Record<string, number>,
  deltaSec: number,
  minSec = INFOSERVICE_MIN_SEC,
): { durations: Record<string, number>; residualSec: number } {
  const valueOf = (value: InfoserviceBreak) => current[value.key] ?? value.currentInfoserviceSec
  const carrying = breaks.filter(value => valueOf(value) > 0)
  // Aucun infoservice dans la boucle : le dernier écart accueille le report,
  // c'est celui qui précède la reprise et le seul qu'on puisse allonger sans
  // décaler un programme au milieu de la plage.
  const targets = carrying.length > 0 ? carrying : breaks.slice(-1)
  if (targets.length === 0) return { durations: current, residualSec: deltaSec }

  const total = targets.reduce((sum, value) => sum + valueOf(value), 0)
  const wanted = total + deltaSec
  const durations = { ...current }
  let assigned = 0
  targets.forEach((value, index) => {
    const isLast = index === targets.length - 1
    // Le dernier absorbe l'arrondi : la somme retombe à la seconde près.
    const share = isLast
      ? wanted - assigned
      : Math.round(total > 0 ? wanted * (valueOf(value) / total) : wanted / targets.length)
    const clamped = Math.max(minSec, share)
    durations[value.key] = clamped
    assigned += clamped
  })
  return { durations, residualSec: wanted - assigned }
}
